import { Script } from '../../proto/generated/indicator'
import { GetMarketNewsResponse } from '../../proto/generated/market_news'
import { filterDuplicateCandles } from '../../util/chartUtils'
import { CalculateMarginResponse } from '../../proto/generated/order'
import api from './axiosClient'
import { ParticipantData, ParticipantDataList } from '../../proto/generated/open-interest'
import { OptionPositionRequest, OptionPositionResponse } from '../../proto/generated/strategy'
import store from '../../store'
import { setIndicatorScript } from '../../reducer/chart.reducer'
import { sanitizeId } from '../../util/indicators'

const inFlightIndicatorScripts = new Map<string, Promise<Script>>()

const fetchIndicatorScriptFromApi = (code: string): Promise<Script> => {
    return new Promise((resolve, reject) => {
        const url = `/indicators/script`
        api.get(url, {
            headers: {
                Accept: 'application/x-protobuf'
            },
            params: {
                code
            },
            responseType: 'arraybuffer'
        })
            .then(async (response) => {
                const decodedData = Script.decode(new Uint8Array(response.data))
                resolve(decodedData)
            })
            .catch((error) => {
                reject(error)
            })
    })
}

export const getIndicatorScript = async (args: { code: string }): Promise<Script> => {
    const cacheKey = sanitizeId(args.code)

    const cachedScript = store.getState().chartStore.indicatorScripts[cacheKey]
    if (cachedScript) {
        return Script.create({ code: args.code, script: cachedScript, type: 'INDICATOR' })
    }

    const inFlight = inFlightIndicatorScripts.get(cacheKey)
    if (inFlight) {
        return inFlight
    }

    const request = fetchIndicatorScriptFromApi(args.code)
        .then((scriptResponse) => {
            store.dispatch(setIndicatorScript({ code: cacheKey, script: scriptResponse.script }))
            return scriptResponse
        })
        .finally(() => {
            inFlightIndicatorScripts.delete(cacheKey)
        })

    inFlightIndicatorScripts.set(cacheKey, request)
    return request
}

export const getMarketNews = async (args: { pageNumber: number; pageSize: number; companyCode: string }): Promise<GetMarketNewsResponse> => {
    return new Promise((resolve, reject) => {
        const url = `/marketnews`
        api.post(
            url,
            JSON.stringify({
                pageNumber: args.pageNumber,
                pageSize: args.pageSize,
                companyCode: args.companyCode
            }),
            {
                headers: {
                    Accept: 'application/x-protobuf'
                },
                responseType: 'arraybuffer'
            }
        )
            .then(async (response) => {
                const decodedData = GetMarketNewsResponse.decode(new Uint8Array(response.data))
                resolve(decodedData)
            })
            .catch((error) => {
                reject(error)
            })
    })
}


export const getParticipantData = async (): Promise<ParticipantData[]> => {
    return new Promise((resolve, reject) => {
        const url = `/participant-data`
        api.get(url, {
            headers: {
                'Accept': 'application/x-protobuf'
            },
            responseType: 'arraybuffer'
        })
            .then(async (response) => {
                const participantList = ParticipantDataList.decode(new Uint8Array(response.data))
                resolve(participantList.list as ParticipantData[])
            })
            .catch((error) => {
                reject(error)
            })
    })
}

export const getMargin = async (args: { body: any }): Promise<CalculateMarginResponse> => {
    return new Promise((resolve, reject) => {
        const url = `/margincalculator/calculate`
        api.post(
            url,
            JSON.stringify(args.body),
            {
                headers: {
                    Accept: 'application/x-protobuf'
                },
                responseType: 'arraybuffer'
            }
        )
            .then(async (response) => {
                const marginData = CalculateMarginResponse.decode(new Uint8Array(response.data))
                resolve(marginData)
            })
            .catch((error) => {
                reject(error)
            })
    })
}

export const getStrategyName = async (args: { body: OptionPositionRequest }): Promise<OptionPositionResponse> => {
    return new Promise((resolve, reject) => {
        const url = `/v2/strategy/name`
        api.post(url,JSON.stringify(args.body),
            {
                headers: {
                    Accept: 'application/x-protobuf'
                },
                responseType: 'arraybuffer'
            }
        ).then((response) => {
                const data = OptionPositionResponse.decode(
                    new Uint8Array(response.data)
                )
                resolve(data)
            })
            .catch((error) => {
                reject(error)
            })
    })
}


export const logExceptions = async (message: any) => {
    return new Promise((resolve, reject) => {
        const url = `/log`
        api.post(
            url,
            JSON.stringify({
                message: JSON.stringify(message),
                timestamp: new Date().getTime()
            }),
            {
                headers: {
                    Accept: 'application/x-protobuf'
                },
                responseType: 'arraybuffer'
            }
        )
            .then(async (response) => {
                resolve(response)
            })
            .catch((error) => {
                reject(error)
            })
    })
};